import {
	ClientPortfolioInvestmentFrequencyEnum,
	ClientPortfolioInvestmentTypeEnum,
	CurrencyEnum,
} from "web_myplann/Infrastructure/Api/Api";
import { Resources } from "web_myplann/Infrastructure/Translations/Resources";
import type { TranslateFunction } from "web_myplann/Shared/TranslateFunction";
import { DateFormat } from "web_myplann/Shared/Utils/dateFormats";
import { optionalDateAsString } from "web_myplann/Shared/Validation/optionalDateAsString";
import { optionalPositiveFloat } from "web_myplann/Shared/Validation/optionalPositiveFloat";
import { ValidationLimits } from "web_myplann/Shared/Validation/validationLimits";
import { z } from "zod";

const refineInstructionDateOrder =
	(endBeforeStart: string) =>
	(
		data: { date: string | null; endedAt: string | null },
		ctx: z.RefinementCtx,
	): void => {
		if (data.date && data.endedAt && data.endedAt < data.date) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				path: ["endedAt"],
				message: endBeforeStart,
			});
		}
	};

const REQUIRED_INSTRUCTION_FIELDS = ["date", "amount", "frequency"] as const;

type RequiredInstructionMessages = {
	required: string;
	positiveInteger: string;
};

export const refineRequiredInstructionFields =
	(messages: RequiredInstructionMessages) =>
	(
		data: Record<(typeof REQUIRED_INSTRUCTION_FIELDS)[number], unknown>,
		ctx: z.RefinementCtx,
	): void => {
		for (const field of REQUIRED_INSTRUCTION_FIELDS) {
			if (data[field] === null) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					path: [field],
					message: messages.required,
				});
			}
		}
		if (typeof data.amount === "number" && !Number.isInteger(data.amount)) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				path: ["amount"],
				message: messages.positiveInteger,
			});
		}
	};

export const getInstructionSchema = (t: TranslateFunction) => {
	return z
		.object({
			id: z.number().nullable(),
			investmentType: z
				.nativeEnum(ClientPortfolioInvestmentTypeEnum)
				.nullable()
				.refine(
					(value) => value !== null,
					t(Resources.Portfolio.AddShared.Validations.required),
				),
			date: optionalDateAsString(DateFormat.Iso),
			endedAt: optionalDateAsString(DateFormat.Iso),
			amount: optionalPositiveFloat(ValidationLimits.Amount.Max, {
				positiveMessage: t(
					Resources.Portfolio.AddShared.Validations.positiveNumber,
				),
				maxMessage: t(Resources.Portfolio.AddShared.Validations.maxValue),
			}),
			currency: z.nativeEnum(CurrencyEnum),
			frequency: z
				.nativeEnum(ClientPortfolioInvestmentFrequencyEnum)
				.nullable(),
		})
		.superRefine(
			refineInstructionDateOrder(
				t(Resources.Portfolio.AddShared.Validations.endBeforeStart),
			),
		);
};

export type InstructionInput = z.input<ReturnType<typeof getInstructionSchema>>;
export type InstructionOutput = z.output<
	ReturnType<typeof getInstructionSchema>
>;
